import { saveAgentSnapshot } from './snapshots.js';
/* eslint-disable unicorn/prevent-abbreviations */
/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable unicorn/require-post-message-target-origin */
import type { Annotation } from '@studio/design-system/video-annotation';
// Spec 4 (D1): state-sync race attribution.
import { beginRendererOperation, markRendererMilestone } from '#~/boot/renderer-startup.js';
import { recordClaimBootstrapPhase } from '#~/test-probes/claim-bootstrap-observation.js';
import type { Agent, Status } from '@studio/models/agent';
import type { AppId } from '@studio/models/app';
import type { Device, Model } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import {
  AccessReservation,
  ClientType,
  ConnectionType,
  State,
  StatusDiscovery
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import { AutoTrackingReservation } from '@tryiris-ai/common.protojs/studio/v1/studio';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { SyncSubordinate } from '@tryiris-ai/studio.state-sync-ts';
import Emittery from 'emittery';
import { Mutex } from 'async-mutex';
import { isEqual, noop } from 'lodash-es';
import type { NatsConnection, NatsError, Subscription } from 'nats.ws';
import type { ReadonlyDeep } from 'type-fest';
import { fetchModelConfig } from '../api/agent/registry.js';
import { HeartbeatInterval } from './constants.js';
import type { EventData, Proxy, SaveOutcome, Transaction } from './types.js';
import { isMailroomTransportFailure, toHexFromId, toIdFromHex } from '#~/api/index.js';
import { getData } from '#~/api/storage/index.js';
import type { AutoTrackingManager } from '#~/auto-tracking/index.js';
import { StudioError } from '#~/error/index.js';
import { logger } from '#~/logger/index.js';
import {
  AgentDirectProtocolChanged,
  AgentDirectOperationModeChanged,
  AgentDirectTrackTimestamp,
  AgentDirectDeviceSettingsProfileApplied
} from '@tryiris-ai/common.protojs/api/v1/agent_direct';
import { SharedTimestampManager } from '#~/auto-tracking/provider/timestamp-manager.js';
import { CurrentUser } from '#~/auth/synchronizer.js';
import type { Msg } from 'nats.ws';
import type { User } from '@studio/models/user';
import { nats } from '#~/nats/index.js';
import type { NewtsConnection } from '#~/newts/connection.js';
import HeartbeatWorker from './heartbeat.js?worker';
import { generateId } from '@studio/models';

import { visibleOnline } from '#~/livekit/livekit.js';
import { getUserIsActive } from '#~/livekit/user-activity.js';

import { isElectronApp } from '#~/electron/utils.js';
// ENG-4154: read the IdentityState store, not a per-consumer bridge poll.
import { resolveLinkedAgentId } from '#~/electron/index.js';
import { addProxySubscriber } from '#~/electron/api.js';
import { AutoTrackingWorkerManagerImpl } from './auto-tracking-worker-manager.js';
import { writeDiscoveringReservation } from './discovering-reservation.js';
import { notifyOperationMode } from './components/monitoring/settings/notification.js';
import { notifyProfileApplied } from './components/monitoring/settings/notification.js';
import { depot } from '#~/depot/index.js';
import { assertTrialCameraMutationAllowed } from '#~/api/billing/camera-actions.js';
import { captureEncoderPostHogMessage, encoderPostHogSubject } from '#~/posthog/encoder-errors.js';

const MAX_CLOCK_DRIFT = 1000 * 60 * 5; // 5 minutes
const MAX_PING_CACHE = 30_000;
const MAX_PING_TIMEOUT = 5000;

// Same-agent writes include replacement proxies; idle entries retain neither credentials nor state.
const storageWrites = new Map<Agent['id'], { mutex: Mutex; pending: number }>();

// Merge saved rows missing from live control without creating a broker/proxy import cycle.
// An absent resolver means no retained rows.
const NO_RETAINED_ROWS: ReadonlySet<Device['id']> = new Set();
let retainedRowResolver: ((proxy: Proxy) => ReadonlySet<Device['id']>) | undefined;

// Legacy state-sync encodes an empty repeated enum as scalar zero, which the generated
// builder cannot map. Restore only that known empty shape on a copy; keep real warning
// lists and live state intact. Every storage writer (save, pruneStoredSource) applies it
// (wire-contract-changes-must-not-break-old-firmware, ai.knowledge/global).
const withEmptyLegacyHealthDetail = (
  live: ReadonlyDeep<NonNullable<State['control']>>
): ReadonlyDeep<NonNullable<State['control']>> =>
  live.studio
    ? {
        ...live,
        studio: {
          ...live.studio,
          device: Object.fromEntries(
            Object.entries(live.studio.device ?? {}).map(([id, device]) => {
              const details: unknown = device.configHealthDetail;
              return [id, details === 0 ? { ...device, configHealthDetail: [] } : device];
            })
          )
        }
      }
    : live;

/** Register the retained-row resolver; undefined clears it. */
export const setRetainedRowResolver = (
  resolver: ((proxy: Proxy) => ReadonlySet<Device['id']>) | undefined
): void => {
  retainedRowResolver = resolver;
};

/** Agent proxy implementation. */
export class ProxyImpl extends Emittery<EventData> implements Proxy {
  /** Agent identifier. */
  public readonly id: Agent['id'];

  /** Room identifier. */
  public readonly roomId: Room['id'];

  /** Embedded agent flag. */
  public readonly embedded: boolean;

  /** Icon URL */
  public iconUrl: T_Maybe<string>;

  /** Model identifier. */
  public readonly modelId: T_Maybe<Uint8Array>;

  /** Agent name. */
  public readonly name: T_Maybe<string>;

  /** Brand identifier. */
  public readonly brandId: T_Maybe<Uint8Array>;

  /** Abort controller. */
  #abortController: T_Maybe<AbortController>;

  /** Unique application ID. */
  readonly #appId: AppId;

  /** Unique session ID. */
  #sessionId: AppId;

  /** Resolved session ID. */
  #resolvedSessionId?: boolean;

  /** Auto-tracking. */
  #autoTrackingManager?: AutoTrackingManager;

  /** Dirty flag. */
  #dirty: boolean;

  /** Heartbeat worker. */
  #heartbeat: T_Maybe<Worker>;

  /** Nats connection. */
  readonly #natsConnection: NatsConnection;

  /** Indicates that state synchronization has been started. */
  #started: boolean = false;

  /** State sync subordinate. */
  readonly #subordinate: SyncSubordinate<State>;

  #heartbeatPingSubscription: T_Maybe<Subscription>;

  #tokenGenerator: () => Promise<string>;

  #protocolChangedSubscription: T_Maybe<Subscription>;

  #operationModeChangeSubscription: T_Maybe<Subscription>;

  #profileAppliedSubscription: T_Maybe<Subscription>;

  #encoderPostHogErrorSubscription: T_Maybe<Subscription>;

  #activeDeviceID: T_Maybe<Uint8Array>;

  #autoTrackingWorkerManager: AutoTrackingWorkerManagerImpl;

  /** Last value passed to `setDiscovering`. Heartbeats re-assert it. */
  #discovering: boolean = false;

  /** A heartbeat was skipped while state sync was not ready; send one when sync returns. */
  #heartbeatDeferred: boolean = false;

  /**
   * Construct the agent proxy.
   *
   * @param roomId - Room identifier.
   * @param id - Agent identifier.
   * @param args - Constructor arguments.
   * @param args.appId - Application identifier.
   * @param args.autoTrackingManager - Auto-tracking manager.
   * @param args.conn - NATS connection.
   * @param args.subordinate - State sync subordinate.
   * @param args.embedded - Indicates that the agent is embedded.
   * @param args.modelId - Model identifier for embedded agents.
   * @param args.brandId - Brand identifier for embedded agents.
   * @param args.name - Agent name.
   * @param args.tokenGenerator - Token generator.
   */
  public constructor(
    roomId: Room['id'],
    id: Agent['id'],
    args: {
      appId: AppId;
      brandId?: T_Maybe<Uint8Array>;
      conn: NatsConnection;
      embedded?: T_Maybe<boolean>;
      modelId?: T_Maybe<Uint8Array>;
      name?: T_Maybe<string>;
      subordinate: SyncSubordinate<State>;
      tokenGenerator: () => Promise<string>;
    }
  ) {
    super();

    this.id = id;
    this.roomId = roomId;
    this.#appId = args.appId;
    this.#sessionId = args.appId;
    this.#dirty = false;
    this.#natsConnection = args.conn;
    this.#subordinate = args.subordinate;

    this.embedded = !!args.embedded;
    this.modelId = args.modelId;
    this.brandId = args.brandId;

    this.name = args.name;
    this.#tokenGenerator = args.tokenGenerator;

    void this.#subscribeToTimestamps();
    this.#listenForHeartbeatPing();
    void this.#loadDeviceConfig();
    this.#listenForConnectionTimeoutEvents();
    this.#listenForOperationModeChange();
    this.#listenForProfileApplied();
    this.#listenForEncoderPostHogErrors();

    this.#monitorLocalConnection();

    // Room events can also resume a deferred heartbeat; authority acceptance does not depend on them.
    this.on('state.tx', () => this.#resumeDeferredHeartbeat());
    this.on('state.reset', () => this.#resumeDeferredHeartbeat());

    // @ts-expect-error
    window.proxies = window.proxies || {};
    // @ts-expect-error
    window.proxies[this.id] = [this, ...(window.proxies[this.id] ?? [])];

    this.#autoTrackingWorkerManager = new AutoTrackingWorkerManagerImpl(this);
  }

  public setAutoTrackingManager(manager: AutoTrackingManager): void {
    this.#autoTrackingManager = manager;
  }

  /**
   * Returns the NATS connection associated with this agent product.
   *
   * @returns NATS connection.
   */
  public get connection(): NatsConnection {
    return this.#natsConnection;
  }

  /**
   * Indicates that the agent has been mutated since it was last saved.
   *
   * @returns `true` if the agent has been mutated since it was last saved, `false` otherwise.
   */
  public get isDirty(): boolean {
    return this.#dirty;
  }

  /**
   * Returns the agent started state.
   *
   * @returns Boolean.
   */
  public get isStarted(): boolean {
    return this.#started;
  }

  /**
   * Returns the subordinate state.
   *
   * @returns The subordinate state.
   */
  public get state(): ReadonlyDeep<State> {
    return this.#subordinate.state.current.message;
  }

  /**
   * Creates a new auto-tracking reservation.
   *
   * @param deviceId - Device identifier.
   */
  public async createAutoTrackingReservation(deviceId: Device['id']): Promise<void> {
    const id = toIdFromHex(deviceId);

    if (id) {
      await this.#autoTrackingManager!.createReservation(id);
    }
  }

  public async transferToAutoTrackingReservation(deviceId: Device['id']): Promise<void> {
    const id = toIdFromHex(deviceId);

    if (id) {
      await this.#autoTrackingManager!.transferReservation(id);
    }
  }

  /**
   * Destroys an auto-tracking reservation.
   *
   * @param deviceId - Device identifier.
   */
  public async destroyAutoTrackingReservation(deviceId: Device['id']): Promise<void> {
    const id = toIdFromHex(deviceId);

    if (id) {
      await this.#autoTrackingManager!.destroyReservation(id);
    }
  }

  #deviceFocusTimeout: NodeJS.Timeout | undefined;

  public focusDevice(deviceId: Device['id']): void {
    clearTimeout(this.#deviceFocusTimeout!);

    this.#deviceFocusTimeout = setTimeout(() => {
      const id = toIdFromHex(deviceId);

      if (id?.toString() === this.#activeDeviceID?.toString()) {
        return;
      }

      if (id) {
        this.#activeDeviceID = id;
        this.#sendHeartbeat();
      }
    }, 250);
  }

  public getSessionId(): T_Maybe<AppId> {
    return this.#sessionId;
  }

  /**
   * Gets an auto-tracking annotations.
   *
   * @param deviceId - Device identifier.
   * @returns The auto-tracking subordinate.
   */
  public getAutoTrackingAnnotations(deviceId: Device['id']): T_Maybe<Annotation[]> {
    const id = toIdFromHex(deviceId);

    return id ? this.#autoTrackingManager!.getAnnotationsForCameraId(id) : undefined;
  }

  /**
   * Perform a state mutation on the agent.
   *
   * @param set - State mutation set delta.
   * @param unset - State mutation unset delta.
   * @param optimistic - Perform an optimistic mutation.
   */
  public async mutate(
    set: DeepPartial<State>,
    unset: DeepPartial<State>,
    optimistic: boolean = true
  ): Promise<void> {
    if (unset === undefined) {
      unset = {};
    }
    assertTrialCameraMutationAllowed(this.roomId, this.id, set, this.state);

    // E2E-only observation of the request this UI is actually submitting,
    // recorded before dispatch because that is the only point at which the
    // submitted command exists in full. A hardware test that reads only
    // receipts has to infer what was asked for from what completed, which
    // cannot tell "this click produced this operation" from "an operation
    // completed near this click".
    //
    // Reached through the same globalThis hook the other renderer probes
    // install, NOT an import: a static dependency from the agent layer onto a
    // test probe puts a diagnostic in the module graph of every suite that
    // mocks this file. Absent hook, absent cost — and it cannot throw into the
    // operator's action, because the probe owns the try/catch.
    // See test-probes/native-preset-submission-probe.ts.
    (
      globalThis as {
        __irisNativePresetSubmissions?: { record?: (agentId: Agent['id'], set: unknown) => void };
      }
    ).__irisNativePresetSubmissions?.record?.(this.id, set);

    await this.#subordinate.requestMutation(set, unset, optimistic);

    this.#dirty = true;
  }

  /**
   * Mutates the auto-tracking reservation.
   *
   * @param reservations - Reservations.
   */
  public mutateAutoTrackingReservations(
    unsafeReservations: Record<string, AutoTrackingReservation>
  ): void {
    const reservations: Record<string, AutoTrackingReservation> = {};

    for (const key of Object.keys(unsafeReservations)) {
      const reservation = unsafeReservations[key];

      if (!reservation) {
        continue;
      }

      reservations[key] = reservation;
    }

    this.#autoTrackingManager!.handleMutation(reservations);
  }

  public async mutateAdvertisedDevices(devices: Record<string, StatusDiscovery>): Promise<number> {
    const currentlyLinkedMachine = await resolveLinkedAgentId();

    if (currentlyLinkedMachine === undefined) {
      return 0;
    }

    if (!isElectronApp() || currentlyLinkedMachine !== this.id) {
      return -1;
    }

    const currentDevices =
      this.state.control?.reservation?.[this.#sessionId]?.advertisedDiscoveries ?? {};
    const staleDevices = Object.keys(currentDevices).filter((key) => !devices[key]);

    // Forward entries that are new or changed (deep-equal via isEqual); filtering to
    // new keys only would miss later mDNS enrichments the encoder needs.
    const changedDevices: Record<string, StatusDiscovery> = {};
    for (const [key, value] of Object.entries(devices)) {
      const existing = currentDevices[key];
      if (!existing || !isEqual(existing, value)) {
        changedDevices[key] = value;
      }
    }

    // Skip mutation entirely if nothing changed (no new/changed entries, no stale removals).
    if (Object.keys(changedDevices).length === 0 && staleDevices.length === 0) {
      return 1;
    }

    await this.#subordinate.requestMutation(
      {
        control: {
          reservation: {
            [this.#sessionId]: {
              advertisedDiscoveries: { ...changedDevices }
            }
          }
        }
      },
      staleDevices.length === 0
        ? {}
        : {
            control: {
              reservation: {
                [this.#sessionId]: {
                  advertisedDiscoveries: Object.fromEntries(staleDevices.map((key) => [key, {}]))
                }
              }
            }
          },
      true
    );

    return 1;
  }

  /**
   * Persist control in agent order. A removal holds this order through authority confirmation
   * so an ordinary save cannot restore the pre-removal snapshot in the intervening window.
   *
   * Resolves with an explicit outcome so a skip is never mistaken for a successful write: `'saved'`
   * when storage now reflects the requested state, `'skipped'` when the write was deliberately not
   * persisted (absent live control, or saved sources that could not be read). The skip guard is
   * intentional (rule: verify-design-intent-before-removal, ai.knowledge/global) and every exit
   * honours the outcome (rule: settle-every-terminal-branch, ai.knowledge/global). A removal never
   * resolves 'skipped' — it rejects so its caller learns the delete did not persist.
   */
  public async save(
    token: string,
    force?: boolean,
    removal?: Parameters<Proxy['save']>[2]
  ): Promise<SaveOutcome> {
    const writes = storageWrites.get(this.id) ?? { mutex: new Mutex(), pending: 0 };
    storageWrites.set(this.id, writes);
    writes.pending++;
    const signal = this.#abortController?.signal;
    try {
      // return await (not bare return): the finally below must run after the write settles.
      return await writes.mutex.runExclusive(async (): Promise<SaveOutcome> => {
        if (signal?.aborted || (removal && !removal.current()))
          throw new DOMException('Source owner retired', 'AbortError');
        // Read at execution, after preceding saves/removals settle. The generated builder
        // clones control so pruning persistent data cannot optimistically delete a live row.
        const { control } = this.state;
        // Retained rows may be saved even while absent from live control.
        const retainedIds = retainedRowResolver?.(this) ?? NO_RETAINED_ROWS;
        if (!control) {
          // Never overwrite storage from absent live control; deletion must fail explicitly.
          if (removal) throw new Error('Source control is unavailable');
          return 'skipped';
        }
        if (!this.#dirty && !force && !removal) return 'saved';
        // Read storage only for retained rows missing from live control; live rows win.
        // Healthy saves therefore need no extra storage read.
        const missingFrom = (live: ReadonlyDeep<NonNullable<State['control']>>): Device['id'][] =>
          [...retainedIds].filter(
            (id) => !(removal && id === removal.deviceId) && !live.device?.[id]
          );
        const missing = missingFrom(control);
        let stored: State | undefined;
        if (missing.length) {
          try {
            stored = State.decode(await getData(toIdFromHex(this.id)!, token));
          } catch (cause) {
            // Skip unreadable retained state rather than erase it; keep dirty state for the next save.
            // Deletion must reject instead of claiming persistence.
            if (removal) throw new StudioError('Unable to save the agent state.', { cause });
            logger.warning('Skipped saving agent state: saved sources could not be read.', cause);
            return 'skipped';
          }
        }
        // Re-encode current state on storage conflicts, reapplying removal and retained-row merge.
        const encode = (): Uint8Array => {
          // The generated builder reads and clones its input. Its mutable-array signature does
          // not express the subordinate's readonly view (notably configHealthDetail).
          const snapshot = State.fromPartial({
            control: withEmptyLegacyHealthDetail(this.state.control ?? control) as State['control']
          });
          if (removal) {
            delete snapshot.control!.device[removal.deviceId];
            if (snapshot.control!.studio) delete snapshot.control!.studio.device[removal.deviceId];
          }
          for (const id of missingFrom(snapshot.control!)) {
            const storedDevice = stored?.control?.device?.[id];
            if (!storedDevice) continue;
            snapshot.control!.device[id] = storedDevice;
            const storedStudioDevice = stored?.control?.studio?.device?.[id];
            if (storedStudioDevice) {
              // Copy only this saved device entry; adopting the stored studio block would revive stale settings.
              snapshot.control!.studio ??= State.fromPartial({
                control: { studio: {} }
              }).control!.studio!;
              snapshot.control!.studio.device[id] = storedStudioDevice;
            }
          }
          // Discovery evidence is ephemeral and must never enter saved source intent.
          for (const reservation of Object.values(snapshot.control?.reservation ?? {})) {
            reservation.advertisedDiscoveries = {};
          }
          return State.encode(snapshot).finish();
        };
        await saveAgentSnapshot(
          this.id,
          {
            encode,
            // Conflict retries need current authority and stored copies for every missing retained row.
            canRetry: () =>
              this.#subordinate.isInSync() &&
              missingFrom(this.state.control ?? control).every(
                (id) => !!stored?.control?.device?.[id]
              )
          },
          token,
          this
        ).catch((cause: Error) => {
          throw new StudioError('Unable to save the agent state.', { cause });
        });
        if (removal) {
          if (signal?.aborted || !removal.current())
            throw new DOMException('Source owner retired', 'AbortError');
          // Storage is already committed. Authority failure rejects the operation without
          // pretending a second, fallible storage write could atomically roll it back.
          await removal.commit();
        }
        return 'saved';
      });
    } finally {
      // Rejection must release the queue for later saves.
      if (--writes.pending === 0) storageWrites.delete(this.id);
    }
  }

  // Delete a retained source without requiring live control, serialized with ordinary saves.
  // Preserve peers and overlay live edits before pruning the target.
  public async pruneStoredSource(
    deviceId: Device['id'],
    token: string,
    current: () => boolean
  ): Promise<void> {
    const writes = storageWrites.get(this.id) ?? { mutex: new Mutex(), pending: 0 };
    storageWrites.set(this.id, writes);
    writes.pending++;
    try {
      await writes.mutex.runExclusive(async () => {
        if (!current()) throw new DOMException('Source owner retired', 'AbortError');
        // The persisted snapshot is the source of truth for retained rows; read-modify-write it.
        const snapshot = State.decode(await getData(toIdFromHex(this.id)!, token));
        if (!snapshot.control) return;
        // Overlay any live rows so an in-progress edit to another source is not lost from storage.
        const live = this.state.control && withEmptyLegacyHealthDetail(this.state.control);
        if (live) {
          for (const [id, device] of Object.entries(live.device ?? {})) {
            if (device)
              snapshot.control.device[id] = device as (typeof snapshot.control.device)[string];
          }
          if (live.studio && snapshot.control.studio) {
            for (const [id, device] of Object.entries(live.studio.device ?? {})) {
              if (device)
                snapshot.control.studio.device[id] =
                  device as (typeof snapshot.control.studio.device)[string];
            }
          }
        }
        delete snapshot.control.device[deviceId];
        if (snapshot.control.studio) delete snapshot.control.studio.device[deviceId];
        if (!current()) throw new DOMException('Source owner retired', 'AbortError');
        await saveAgentSnapshot(this.id, State.encode(snapshot).finish(), token, this).catch(
          (cause: Error) => {
            throw new StudioError('Unable to save the agent state.', { cause });
          }
        );
      });
    } finally {
      if (--writes.pending === 0) storageWrites.delete(this.id);
    }
  }

  #starting: Promise<void> | undefined;
  #startTxCleanup: (() => void) | undefined;

  /** Join state-sync startup; its bounded bootstrap wait is not authority readiness. */
  public start(): Promise<void> {
    if (this.#started) return Promise.resolve();
    if (this.#starting) return this.#starting;
    // A previous failed scope can still own heartbeat/listener work.
    this.stop();
    const controller = new AbortController();
    this.#abortController = controller;
    const current = (): boolean =>
      this.#abortController === controller && !controller.signal.aborted;
    // Presence can stop state delivery again after fallback recovery. Keep one
    // scoped rearm listener, and retire it with the startup controller.
    let listeningForRecovery = false;
    const listenForRecovery = (): void => {
      this.#listenForStateTx();
      if (listeningForRecovery) return;
      listeningForRecovery = true;
      const rearm = (): void => {
        if (current()) this.#listenForStateTx();
      };
      this.on('state.stopped', rearm);
      controller.signal.addEventListener('abort', () => this.off('state.stopped', rearm), {
        once: true
      });
    };
    let rawPending = true;
    let waitFinished = false;
    const attempt = Promise.resolve().then(async () => {
      if (!current()) return;
      // Close the idempotent state-sync span on every terminal path.
      const closeStateSync = beginRendererOperation('proxy.state-sync-race', 'agent');
      let timer: ReturnType<typeof setTimeout> | undefined;
      let onAbort = (): void => undefined;
      const report = (cause: unknown): void => {
        // Observation failures must not strand the shared startup primitive.
        try {
          logger.critical(
            new StudioError('Unable to start agent state synchronization.', { cause })
          );
        } catch {}
        void this.emit('state.error', cause as Error).catch(() => undefined);
      };
      try {
        // Subscribe before run: only accepted current-owner snapshots establish startup readiness.
        listenForRecovery();
        const raw = Promise.resolve().then(() => {
          if (!current()) return;
          return this.#subordinate.run();
        });
        const released = (): void => {
          rawPending = false;
          if (waitFinished && this.#starting === attempt) this.#starting = undefined;
        };
        // Promise fulfillment is not acceptance. Read the canonical receipt so
        // delayed transaction forwarding cannot hide a completed current snapshot.
        void raw.then(() => {
          released();
          if (!current()) return;
          // An answered init closes cold-boot re-advertising even if the bridge goes silent.
          // Readiness still requires the accepted snapshot receipt below.
          this.#authorityAnswered = true;
          this.acceptAuthoritySnapshot(this.#subordinate.acceptedSnapshot);
        }, released);
        addProxySubscriber(this);
        const outcome = await Promise.race([
          raw.then(() => 'completed' as const),
          new Promise<'pending'>((resolve) => {
            timer = setTimeout(() => resolve('pending'), 5000);
          }),
          new Promise<'cancelled'>((resolve) => {
            onAbort = () => resolve('cancelled');
            controller.signal.addEventListener('abort', onAbort, { once: true });
            if (controller.signal.aborted) onAbort();
          })
        ]);
        if (!current() || outcome === 'cancelled') {
          closeStateSync('cancelled');
          return;
        }
        // Record WHICH arm of the race won: a 5s backstop win is not completed synchronization.
        closeStateSync(this.#authorityAnswered ? 'ok' : 'cancelled');
        recordClaimBootstrapPhase(
          this,
          undefined,
          this.#authorityAnswered ? 'state-sync-answered' : 'state-sync-backstop',
          'returned'
        );
        markRendererMilestone(
          this.#authorityAnswered ? 'proxy.state-sync-answered' : 'proxy.state-sync-backstop'
        );
        if (outcome === 'pending') {
          // Preserve the existing 5s bootstrap continuation, but neither mark
          // authority ready nor issue a second raw run while this one is pending.
          report(new Error('State synchronization is still pending.'));
          listenForRecovery();
        }
      } catch (cause) {
        closeStateSync('error');
        recordClaimBootstrapPhase(this, undefined, 'state-sync-error', 'threw');
        if (!current()) return;
        report(cause);
        listenForRecovery();
      } finally {
        closeStateSync('cancelled');
        clearTimeout(timer);
        controller.signal.removeEventListener('abort', onAbort);
        waitFinished = true;
        if (!rawPending && this.#starting === attempt) this.#starting = undefined;
      }
      if (!current()) return;
      try {
        this.#startHeartbeatLoop();
        markRendererMilestone('proxy.auto-tracking-controller-created');
        this.#autoTrackingWorkerManager.init();
      } catch (cause) {
        report(cause);
      }
    });
    this.#starting = attempt;
    return attempt;
  }

  /** Stops the agent state synchronization. */
  public stop(): void {
    if (!this.#abortController || this.#abortController.signal.aborted) return;
    // Retire before calling SDK code: even a thrown stop must fence late run/tx.
    this.#abortController.abort();
    this.#starting = undefined;
    this.#startTxCleanup?.();
    // Cleanup is best effort per resource; an SDK/observer failure cannot keep
    // another subscription alive or prevent the stopped state from settling.
    const retire = (cleanup: () => void): void => {
      try {
        cleanup();
      } catch (error) {
        try {
          logger.critical(
            new StudioError('Unable to clean up agent state synchronization.', { cause: error })
          );
        } catch {
          /* Teardown remains authoritative. */
        }
      }
    };
    retire(() => this.#heartbeat?.terminate());
    this.#heartbeat = undefined;
    retire(() => this.#subordinate.stop());
    retire(() => this.#heartbeatPingSubscription?.unsubscribe());
    this.#heartbeatPingSubscription = undefined;
    if (this.localConnectionMonitoringLoop) {
      clearInterval(this.localConnectionMonitoringLoop);
      this.localConnectionMonitoringLoop = undefined;
    }
    retire(() => this.#protocolChangedSubscription?.unsubscribe());
    this.#protocolChangedSubscription = undefined;
    retire(() => this.#operationModeChangeSubscription?.unsubscribe());
    this.#operationModeChangeSubscription = undefined;
    retire(() => this.#profileAppliedSubscription?.unsubscribe());
    this.#profileAppliedSubscription = undefined;
    retire(() => this.#encoderPostHogErrorSubscription?.unsubscribe());
    this.#encoderPostHogErrorSubscription = undefined;
    this.#started = false;
    void this.emit('stopped').catch(() => undefined);
  }

  /** Listen for the state transaction. */
  #listenForStateTx(): void {
    this.#startTxCleanup?.();
    const controller = this.#abortController;
    if (!controller || controller.signal.aborted) return;
    const cleanup = (): void => {
      this.off('state.tx', listener);
      controller.signal.removeEventListener('abort', cleanup);
      if (this.#startTxCleanup === cleanup) this.#startTxCleanup = undefined;
    };
    const listener = (tx: Transaction): void => {
      if (controller !== this.#abortController || controller.signal.aborted) return;
      this.acceptAuthoritySnapshot(tx);
    };
    this.#startTxCleanup = cleanup;
    controller.signal.addEventListener('abort', cleanup, { once: true });
    this.on('state.tx', listener);
  }

  /** @internal Accept readiness independently of room/UI transaction delivery. */
  public acceptAuthoritySnapshot(tx: Transaction | undefined): void {
    // The broker calls this synchronously on authority acceptance, including resets
    // that replace a pending init. Keep current-owner readiness fenced by teardown.
    if (
      !this.#abortController ||
      this.#abortController.signal.aborted ||
      tx?.accepted?.kind !== 'snapshot' ||
      tx.state !== this.#subordinate.state ||
      tx !== this.#subordinate.acceptedSnapshot
    )
      return;
    this.#authorityAnswered = true;
    const wasStarted = this.#started;
    this.#started = true;
    this.#startTxCleanup?.();
    // Recovery must not depend on the room forwarding this accepted snapshot to the UI.
    this.#resumeDeferredHeartbeat();
    if (!wasStarted) void this.emit('state.started').catch(() => undefined);
  }

  #pingSubject(): string {
    return `$direct.peer.${this.#sessionId}.ping`;
  }

  #listenForHeartbeatPing(force?: boolean): void {
    if (this.#heartbeatPingSubscription && force) {
      this.#heartbeatPingSubscription.unsubscribe();
    }

    this.#heartbeatPingSubscription = nats.agentConnection(this.id).subscribe(this.#pingSubject(), {
      callback: (_, message: Msg) => {
        const msg = AgentDirectTrackTimestamp.encode(
          AgentDirectTrackTimestamp.fromPartial({
            timestamp: Date.now()
          })
        ).finish();

        nats.agentConnection(this.id).publish(message.reply!, msg);
      }
    });

    if (this.#protocolChangedSubscription != undefined) {
      this.#protocolChangedSubscription.unsubscribe();
    }

    try {
      this.#protocolChangedSubscription = nats
        .agentConnection(this.id)
        .subscribe(`$direct.broadcast.${this.id}.protocol.changed`, {
          callback: (_: NatsError | null, message: Msg) => {
            const msg = AgentDirectProtocolChanged.decode(message.data);

            void this.emit('protocol.changed', msg);
          }
        });
    } catch {
      // shhhhh
    }
  }

  private sessionPingMap: Record<
    string,
    {
      timestamp: number;
      pending: boolean;
      result?: boolean;
      callbacks: ((result: boolean) => void)[];
    }
  > = {};

  public async pingSession(
    sessionID: string,
    timeout: number = MAX_PING_TIMEOUT
  ): Promise<boolean> {
    if (
      this.sessionPingMap[sessionID]?.timestamp !== undefined &&
      Date.now() - this.sessionPingMap[sessionID]!.timestamp > MAX_PING_CACHE
    ) {
      delete this.sessionPingMap[sessionID];
    }

    if (this.sessionPingMap[sessionID]?.pending) {
      return new Promise((resolve) => {
        this.sessionPingMap[sessionID]!.callbacks.push(resolve);
      });
    }

    if (this.sessionPingMap[sessionID]?.result !== undefined) {
      return this.sessionPingMap[sessionID]!.result!;
    }

    this.sessionPingMap[sessionID] = {
      timestamp: Date.now(),
      pending: true,
      callbacks: []
    };

    let ok = false;

    try {
      const resp = await nats
        .agentConnection(this.id)
        .request(`$direct.peer.${sessionID}.ping`, new Uint8Array([1]), {
          timeout,
          reply: this.#pingSubject() + '.response.' + generateId()
        });

      const { timestamp } = AgentDirectTrackTimestamp.decode(resp.data);

      const diff = Date.now() - timestamp;

      if (Math.abs(diff) > MAX_CLOCK_DRIFT) {
        return false;
      }

      ok = true;
      return true;
    } catch {
      return false;
    } finally {
      this.sessionPingMap[sessionID]!.result = ok;
      this.sessionPingMap[sessionID]!.pending = false;

      for (const callback of this.sessionPingMap[sessionID]!.callbacks) {
        callback(ok);
      }
    }
  }

  public forcePing(): void {
    this.#sendHeartbeat();

    setTimeout(() => {
      this.#sendHeartbeat();
    }, 2500);
  }

  /**
   * Write `control.reservation[session].discovering` immediately (and remember
   * it so every heartbeat refresh carries the same value).
   *
   * Per rule: wire-contract-changes-must-not-break-old-firmware (ai.knowledge/global)
   * — clearing is `discovering: false` (a set), never an unset. Old encoders
   * ignore the field.
   *
   * See knowledge: state-sync-optimistic-mutations (ai.knowledge/studio.client)
   * — same requestMutation path as the heartbeat.
   */
  public async setDiscovering(on: boolean): Promise<void> {
    this.#discovering = on;

    // Session id is assigned on the first heartbeat. Remember now; that write carries it.
    if (!this.#resolvedSessionId) {
      return;
    }

    try {
      await writeDiscoveringReservation(
        (set, unset) => this.#subordinate.requestMutation(set, unset),
        this.#sessionId,
        on
      );
    } catch (err) {
      // See knowledge: use-canonical-logger-not-console (ai.knowledge/studio.client).
      logger.error('unable to write discovering reservation', err);
    }
  }

  async #resolveSessionId(_userId: User['id']): Promise<[AppId, boolean]> {
    return [this.#appId, false];
  }

  public destroyHeartbeat(): void {
    // Drop the heartbeat an outage owed; sending it would recreate the reservation removed here.
    this.#heartbeatDeferred = false;

    try {
      this.#subordinate
        .requestMutation(
          {},
          {
            control: {
              reservation: {
                [this.#sessionId]: {}
              }
            }
          }
        )
        .catch(noop);
    } catch (e) {
      console.error(e);
    } finally {
      this.#heartbeat?.terminate();
    }
  }

  public async destroyHeartbeatAsync(): Promise<void> {
    this.#heartbeatDeferred = false;

    try {
      await this.#subordinate.requestMutation(
        {},
        {
          control: {
            reservation: {
              [this.#sessionId]: {}
            }
          }
        }
      );
    } catch (e) {
      console.error(e);
    } finally {
      this.#heartbeat?.terminate();
    }
  }
  /*
  #workers: AutoTrackingWorker[] = [];

  registerAutoTrackingWorker(worker: AutoTrackingWorker): void {
    if (this.#workers.includes(worker)) {
      console.log('[$GPU] Worker already registered, bailing:', worker);
      return;
    }

    this.#workers.push(worker);

    worker.registered = true;

    // reset the worker state
    worker.resetState();
  }

  unregisterAutoTrackingWorker(worker: AutoTrackingWorker): void {
    const index = this.#workers.indexOf(worker);

    if (index === -1) {
      return;
    }

    this.#workers.splice(index, 1);

    worker.registered = false;
  }

  hasAutoTrackingWorker(worker: AutoTrackingWorker): boolean {
    return (
      this.#workers.includes(worker) &&
      this.state.control?.studio?.autoTrackingWorker[toHexFromId(worker.id)!] !== undefined
    );
  }

  destroyAutotrackingWorkers(): void {
    for (const worker of this.#workers) {
      worker.shutdown();
      this.unregisterAutoTrackingWorker(worker);
    }
  }

  w: T_Maybe<AutoTrackingWorker>;
  hasHadWorker: boolean = false;
  isInitializingWorker: boolean = false;


  ensureAutoTrackingWorker = async (): Promise<void> => {
    console.log('[$GPU] Ensuring GPU worker:', this.w, this);

    const hasAutoTracking = await PostHogManager.getInstance().getFeatureFlag(
      PostHogFeatureFlag.AUTOTRACKING,
      { defaultValue: false }
    );

    if (!hasAutoTracking) {
      console.log('[$GPU] Bailing, Auto tracking is disabled');
      return;
    }

    if (this.w !== undefined) {
      //if(!this.hasAutoTrackingWorker(this.w) && this.w.registered){
      this.w.stopSubjectTracking();
      //this.w.shutdown();
      this.unregisterAutoTrackingWorker(this.w);

      console.log('[$GPU] Unregistered GPU worker:', this.w);

      this.w = undefined;
      //}
    }

    if (typeof window.MediaStreamTrackProcessor === 'function') {
      if (this.w === undefined && !this.isInitializingWorker) {
        this.isInitializingWorker = true;
        await AutoTrackingWorker.waitForReady();

        this.w = new AutoTrackingWorker(nats.agentConnection(this.id), this, () => {
          console.log('$WORKER', 'worker recreating');

          this.w = undefined;
          this.hasHadWorker = false;
          this.ensureAutoTrackingWorker();
        });

        this.hasHadWorker = true;
        console.log('[$GPU] Started GPU worker:', this.w);
        this.isInitializingWorker = false;
      }
    }

    if (this.w) {
      console.log('[$GPU] Registering GPU worker:', this.w);
      this.registerAutoTrackingWorker(this.w);
    }
  };
*/

  #lastPing: number = 0;

  /** Send the heartbeat. */
  async #sendHeartbeat(isRetry: boolean = false): Promise<void> {
    if (Date.now() - this.#lastPing < 1000 && isRetry) {
      return;
    }

    // Out of sync, requestMutation only parks this write until a 10 s timeout, and the
    // catch below re-parks it 250 ms later, forever, one chain per tick. None of those
    // writes reach the authority, and recovery flushes their stale copies. Defer to one
    // fresh heartbeat instead; sync lost after this check still takes the catch path.
    if (!this.#subordinate.isInSync()) {
      this.#heartbeatDeferred = true;
      return;
    }

    const prevLastPing = Number(this.#lastPing);
    this.#lastPing = Date.now();

    const { user } = CurrentUser;

    let isExistingSession = false;
    let isFirstSessionIDResolution = false;

    if (!this.#resolvedSessionId && user?.id !== undefined) {
      [this.#sessionId, isExistingSession] = await this.#resolveSessionId(user.id);
      this.#resolvedSessionId = true;
      isFirstSessionIDResolution = true;

      this.#listenForHeartbeatPing(true);
    }

    const nameArr = [user?.firstName, user?.lastName].filter(Boolean);

    const unset = Object.entries(this.#subordinate.state.current.message.control?.reservation ?? {})
      .filter(([, value]) => value)
      .filter(([, value]) => value.lastPing < Date.now() - HeartbeatInterval * 2)
      .map(([key]) => key);

    const unsetMap: Record<string, any> = {};

    for (const key of unset) {
      unsetMap[key] = {};
    }

    const currentReservation =
      this.#subordinate.state.current.message.control?.reservation?.[this.#sessionId];

    const connectionType = nats.isLocal
      ? ConnectionType.CONNECTION_TYPE_DIRECT
      : ConnectionType.CONNECTION_TYPE_CLOUD;

    const reservation: DeepPartial<AccessReservation> = {
      lastPing: Date.now(),
      pingSubject: this.#pingSubject(),
      userId: visibleOnline ? toIdFromHex(user?.id) : new Uint8Array(),
      userName: nameArr.length > 0 ? nameArr.join(' ') : undefined,

      activeDeviceId: this.#activeDeviceID ?? new Uint8Array(),

      clientType: ClientType.CLIENT_TYPE_BROWSER,
      isWorker: false,

      isVisible: getUserIsActive(),

      connectionType,

      // Re-assert on every refresh so a heartbeat cannot wipe the flag.
      discovering: this.#discovering
    };

    if (
      (isFirstSessionIDResolution && !isExistingSession) ||
      currentReservation?.joinedAt === undefined ||
      currentReservation?.joinedAt === 0
    ) {
      reservation.joinedAt = Date.now();
    }

    if (user?.profilePhotoUrl === undefined) {
      unsetMap[this.#sessionId] = {
        userProfilePhotoUrl: 'abc'
      };
    } else {
      reservation.userProfilePhotoUrl = user.profilePhotoUrl;
    }

    try {
      await this.#subordinate.requestMutation(
        {
          control: {
            reservation: {
              [this.#sessionId]: reservation
            }
          }
        },
        unset.length === 0
          ? {}
          : {
              control: {
                reservation: unsetMap
              }
            }
      );
    } catch (e) {
      this.#lastPing = prevLastPing;
      setTimeout(() => {
        this.#sendHeartbeat(true);
      }, 250);
    }
  }

  /** Send the one heartbeat an out-of-sync period deferred; it defers again if sync is still down. */
  #resumeDeferredHeartbeat(): void {
    if (!this.#heartbeatDeferred) {
      return;
    }

    // Clear first: the heartbeat's own transaction emits 'state.tx' and would resend forever.
    this.#heartbeatDeferred = false;
    void this.#sendHeartbeat();
  }

  /**
   * Starts the heartbeat loop.
   *
   * @private
   * @returns Void
   */
  #startHeartbeatLoop(): void {
    if (this.#heartbeat) {
      return;
    }

    window.onbeforeunload = () => {
      this.destroyHeartbeat();
      //this.destroyAutotrackingWorkers();
    };

    /*
     * We are going to use a web worker to implement a `setInterval` timer that does not
     * get paused when the browser tab is not active.
     */
    const heartbeat = new HeartbeatWorker();

    this.#heartbeat = heartbeat;

    heartbeat.postMessage({
      interval: HeartbeatInterval,
      message: 'start'
    });

    heartbeat.addEventListener(
      'message',
      () => {
        this.#sendHeartbeat();
      },
      { signal: this.#abortController!.signal }
    );

    this.#sendHeartbeat();
  }

  async #loadDeviceConfig(): Promise<void> {
    const token = await this.#tokenGenerator();

    const { modelId } = this;

    if (!modelId) {
      return;
    }

    let config: Awaited<ReturnType<typeof fetchModelConfig>>;

    try {
      config = await fetchModelConfig(modelId, token);
    } catch (error) {
      /*
       * ENG-4176: this is a boot-time fire-and-forget fetch for a cosmetic
       * icon. A transport blip (MailroomTransportError — connection dropped
       * while reading the body) is not worth an error toast; log a warning
       * and degrade to no icon. Genuine decode errors (and everything else)
       * still propagate so real defects stay visible.
       * Per use-canonical-logger-not-console and reference-rules-in-comments.md.
       */
      if (isMailroomTransportFailure(error)) {
        logger.warning(
          `Unable to load device config for agent ${this.id}: transient transport failure`,
          error
        );

        return;
      }

      throw error;
    }

    const { iconUrl } = config.model ?? {};

    this.iconUrl = iconUrl;
  }

  /**
   * Subscribes to the agent's timestamp messages.
   *
   * @private
   */
  async #subscribeToTimestamps(): Promise<void> {
    const subject = 'agent.timestamp';

    const sub = this.#natsConnection.subscribe(subject, {
      callback: (error, message) => {
        if (error) {
          throw error;
        }

        const { trackId, timestamp, trackTimestamp } = AgentDirectTrackTimestamp.decode(
          message.data
        );

        SharedTimestampManager.setTimestampForAgent(this.id, {
          trackId,
          timestamp,
          trackTimestamp
        });
      }
    });

    //let subscribed = true;

    this.on('state.stopped', () => {
      sub.unsubscribe();
      //subscribed = false;
    });
    /*
    const handleTick = async () => {
      console.log('TS loop');

      const iterRes = await sub[Symbol.asyncIterator]().next();

      const message = iterRes.value;

      console.log('>>', message, 'loop');

      const { timestamp } = AgentDirectTrackTimestamp.decode(message.msg ?? message.data);

      SharedTimestampManager.setTimestampForAgent(this.id, {
        local: new Date(),
        remote: timestamp
      });

      if (subscribed) {
        setTimeout(handleTick);
      }
    };

    setTimeout(handleTick);
    */
  }

  // Probe-before-offline: silence can only move alive → suspect. Presence
  // loss or a single pingAgent() cap-out moves suspect → dead. Room
  // ConnectionState is not an input (studio↔SFU is not agent liveness).
  #livenessVerdict: 'alive' | 'suspect' | 'dead' = 'alive';
  #probeInFlight = false;
  #probeGeneration = 0;
  // One-way latch: set the first time the authority answers a cache sync
  // (`isInSync`) or sends a subscription frame (`lastMessageAt`). Neither
  // primitive alone is "ever answered": `isInSync` drops back to false on a
  // later failed resync, and `lastMessageAt` is stamped only by subscription
  // frames, so a synced-then-silent bridge would otherwise still read as
  // never reached. Never cleared — the boot window closes exactly once.
  // Per rule: regression-tests-for-state-machine-primitives (ai.knowledge/global).
  #authorityAnswered = false;

  #hasAuthorityAnswered(): boolean {
    if (
      !this.#authorityAnswered &&
      (this.#subordinate.isInSync() || this.#subordinate.lastMessageAt() > 0)
    ) {
      this.#authorityAnswered = true;
    }
    return this.#authorityAnswered;
  }

  #listenForConnectionTimeoutEvents(): void {
    const newts = nats.agentConnection(this.id) as NewtsConnection;

    newts.setSuspicionHandler(() => {
      void this.#onConnectionSuspicion();
    });

    newts.setRestoreHandler(() => {
      // Silence is suspicion, not a failed media transport. A first message or
      // a response after a healthy probe must not tear down every local preview.
      // Preserve the paced rebuild for recovery from an actual death verdict.
      // Per architecture-principles: control-message cadence is not media health.
      const recoveringFromDeath = this.#livenessVerdict === 'dead';
      this.#onConnectionRestored();
      if (!recoveringFromDeath) return;

      // Surface failed rebuilds without an unhandled rejection.
      void newts.restartLocalConnection().catch((error) => {
        logger.warning('Local connection recovery request failed.', error);
      });
    });
  }

  #onConnectionRestored(): void {
    this.#probeGeneration += 1;
    this.#probeInFlight = false;
    this.#livenessVerdict = 'alive';
  }

  async #onConnectionSuspicion(): Promise<void> {
    if (this.#livenessVerdict === 'dead') {
      return;
    }

    // Cold-boot window: the state authority has never answered. The initial
    // `$CTRL subscribe` went out before the bridge's encoder was listening
    // (Windows cold start: encoder up ~11s after the studio's first contact)
    // and a fire-and-forget control message is not retried, so silence here
    // means "not reached yet", not a lull on an established session. main
    // re-advertised on every silence tick; keep that for this window only.
    // Declaring dead here would latch until an inbound message, and nothing
    // produces one before the 20s heartbeat — e2e win cold boot stalled
    // exactly 20s and shutdown.spec's 30s budget ran out.
    if (!this.#hasAuthorityAnswered()) {
      (nats.agentConnection(this.id) as NewtsConnection).resetRouter();
      return;
    }

    this.#livenessVerdict = 'suspect';

    if (this.#probeInFlight) {
      return;
    }

    this.#probeInFlight = true;
    const generation = ++this.#probeGeneration;
    const newts = nats.agentConnection(this.id) as NewtsConnection;

    try {
      // Presence is identity in remoteParticipants — do not AND with
      // LiveKit room Connected (that is the studio↔SFU path).
      const present = newts.remoteParticipants('hybrid').has(this.id);

      if (!present) {
        if (generation === this.#probeGeneration) {
          this.#declareDead();
        }
        return;
      }

      const ok = await this.ping();

      if (generation !== this.#probeGeneration) {
        return;
      }

      if (ok) {
        this.#livenessVerdict = 'alive';
        return;
      }

      this.#declareDead();
    } catch {
      // Per rule: settle-every-terminal-branch (ai.knowledge/global) —
      // a thrown ping must settle the same way a false result does.
      // Carried from #1674 (ssr-m4/b4a) into the main verdict machine.
      if (generation !== this.#probeGeneration) {
        return;
      }

      this.#declareDead();
    } finally {
      if (generation === this.#probeGeneration) {
        this.#probeInFlight = false;
      }
    }
  }

  #declareDead(): void {
    if (this.#livenessVerdict === 'dead') {
      return;
    }

    this.#livenessVerdict = 'dead';
    // Only a completed death verdict may drop the newts alive flag (the depot
    // onError path short-circuits on it); silence never does. #1674 (b4a).
    (nats.agentConnection(this.id) as NewtsConnection).noteConnectionDead();
    this.emit('state.error', new Error('Connection inactivity timeout.'));
  }

  #listenForOperationModeChange(): void {
    if (!this.embedded) {
      return;
    }

    if (this.#operationModeChangeSubscription != undefined) {
      this.#operationModeChangeSubscription.unsubscribe();
    }

    try {
      this.#operationModeChangeSubscription = nats
        .agentConnection(this.id)
        .subscribe(`$direct.broadcast.${this.id}.operation.mode.changed`, {
          callback: (_: NatsError | null, message: Msg) => {
            const msg = AgentDirectOperationModeChanged.decode(message.data);
            notifyOperationMode(msg.mode, this.state);
          }
        });
    } catch {
      console.log('Failed to subscribe to operation mode changes.');
    }
  }

  #listenForProfileApplied(): void {
    if (!this.embedded) {
      return;
    }

    if (this.#profileAppliedSubscription != undefined) {
      this.#profileAppliedSubscription.unsubscribe();
    }

    try {
      this.#profileAppliedSubscription = nats
        .agentConnection(this.id)
        .subscribe(`$direct.broadcast.${this.id}.settings.profile.applied`, {
          callback: (_: NatsError | null, message: Msg) => {
            const msg = AgentDirectDeviceSettingsProfileApplied.decode(message.data);
            notifyProfileApplied(msg.profileLevel, msg.deviceId, msg.requiresReboot, this.state);
          }
        });
    } catch {
      console.log('Failed to subscribe to profile applied.');
    }
  }

  #listenForEncoderPostHogErrors(): void {
    this.#encoderPostHogErrorSubscription?.unsubscribe();

    try {
      // Per rule: architecture-principles (ai.knowledge/global) — receive via existing Newts channel.
      this.#encoderPostHogErrorSubscription = this.#natsConnection.subscribe(
        encoderPostHogSubject(this.id),
        {
          callback: (error: NatsError | null, message: Msg) => {
            if (error) {
              logger.warning(`Unable to receive encoder PostHog error: ${error.message}`);
              return;
            }

            const room = depot.getState().roomRecords[this.roomId];
            const deviceIds = Object.keys(this.state.control?.device ?? {});
            // Per ENG-3960 — single-camera encoder failures should attribute canonical errors.
            const deviceId = deviceIds.length === 1 ? deviceIds[0] : undefined;
            // ENG-4445: send the catalog model id WITH the device id, out of the same tree.
            // The device id above is a key of this agent's control state; the room
            // projection the emit site would otherwise look it up in (depot
            // rooms[*].devices) is a FILTERED set — pbToJsDevice (protobuf/device.ts:223)
            // drops any device lacking a committed model, a name, or all media. The
            // Add-Source wizard streams its preview before a model is committed, so those
            // devices never enter the projection and the lookup drops brand and model
            // attribution entirely. Reading the model id here avoids the lookup:
            // control.studio.device[id].model is written for every device this agent holds,
            // transient ones included. (Not a Room-mount lifecycle argument — see the note
            // in posthog/encoder-errors.ts for why that framing is false.)
            const deviceModelId = deviceId
              ? toHexFromId<Model['id']>(this.state.control?.studio?.device?.[deviceId]?.model)
              : undefined;
            captureEncoderPostHogMessage(message.data, {
              agentId: this.id,
              ...(deviceId ? { deviceId } : {}),
              ...(deviceModelId ? { deviceModelId } : {}),
              studioId: this.roomId,
              ...(room?.organizationId ? { orgId: room.organizationId } : {})
            });
          }
        }
      );
    } catch (cause) {
      logger.warning(
        new StudioError('Unable to subscribe to encoder PostHog errors.', {
          cause
        })
      );
    }
  }

  async ping(): Promise<boolean> {
    const newts = nats.agentConnection(this.id) as NewtsConnection;

    return newts.pingAgent();
  }

  getLivenessVerdict(): 'alive' | 'suspect' | 'dead' {
    return this.#livenessVerdict;
  }

  getStatus(): Status {
    const lastMessageAt = this.#subordinate.lastMessageAt();
    const connected = lastMessageAt > Date.now() - 5_000;
    const embedded = this.embedded;

    if (connected) {
      return 'online';
    }

    if (embedded) {
      return 'offline-embedded';
    }

    // lastMessageAt === 0 means this session has never heard the agent, so
    // there is no last-known state to keep: a powered-off bridge must not
    // read 'online' just because no probe has run yet. Verdict gating
    // applies only after the first message. #1674 (ssr-m4/b4a 4cf5686424).
    if (lastMessageAt === 0) {
      return 'offline';
    }

    // Non-embedded: a mutation lull is not offline unless the probe
    // declared the agent dead. The 5s window is not widened.
    if (this.#livenessVerdict !== 'dead') {
      return 'online';
    }

    return 'offline';
  }

  localConnectionMonitoringLoop: NodeJS.Timeout | undefined;
  shouldUseLocalConnection: boolean = false;
  hasConnected: boolean = false;
  // embedded link recovery: one-shot guard so the disconnect-recovery resetRouter() fires once
  // per online->offline transition instead of every 1s tick (see #monitorLocalConnection).
  #recoveredForDisconnect: boolean = false;

  #monitorLocalConnection(): void {
    if (this.localConnectionMonitoringLoop) {
      clearInterval(this.localConnectionMonitoringLoop);
    }

    this.localConnectionMonitoringLoop = setInterval(() => {
      const status = this.getStatus();

      if (status === 'online') {
        this.hasConnected = true;
        this.#recoveredForDisconnect = false;
        return;
      }

      if (!this.hasConnected) {
        return;
      }

      // #1502 (cloud-state-reliability): a cloud mutation lull is not a
      // disconnect — desktop proxies only reach the recovery body on a
      // probe-verified dead verdict. Embedded still edge-triggers on the 5s
      // getStatus gap below.
      if (!this.embedded && this.#livenessVerdict !== 'dead') {
        return;
      }

      if (this.#recoveredForDisconnect) {
        return;
      }

      this.#recoveredForDisconnect = true;
      // #1503 (local-state isolation), Hunter lock: never cut state on
      // silence. Dual state transports stay live and only video fails over,
      // so the recovery body neither flips mode (the T4 cloud-flip rerouted
      // every outbound mutation onto a possibly-dead cloud room — WAN-off
      // incident 2026-08-25) nor resets the router (the embedded reset storm
      // this edge-trigger was built to contain).
      logger.info(
        `monitorLocalConnection status=${status} agent=${this.id}; not flipping mode (dual state)`
      );
    }, 1000);
  }

  getSubordinate(): SyncSubordinate<State> {
    return this.#subordinate;
  }
}
